import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { formatApiError } from '@/lib/formatApiError';
import {
  confirmStoryContext,
  draftStoryScript,
  fetchStoryContext,
  patchStoryContext,
  updateStoryFormat,
} from '@/lib/creationPlan';
import { isCharacterName } from '@/lib/unifiedStoryPipeline';
import { StoryContextReview } from '@/components/createPlan/StoryContextReview';
import { StoryFormatReview } from '@/components/createPlan/StoryFormatReview';

function needsContext(workflow) {
  const state = workflow?.state;
  return state === 'context_review' || state === 'analyzing_sources' || Boolean(workflow?.confirm_requested);
}

function needsFormat(workflow) {
  return workflow?.state === 'format_review' || workflow?.story_format?.status === 'proposed';
}

function needsCharacters(workflow) {
  const state = workflow?.state;
  const formatLocked = workflow?.story_format?.status === 'locked' && workflow?.story_format?.confirmation === 'confirmed';
  return formatLocked && (state === 'format_locked' || state === 'character_review');
}

export function StoryChatQuestionnaire({ plan, onPlan, onScript, onOpenBuilder }) {
  const workflow = plan?.story_workflow || {};
  const showContext = needsContext(workflow);
  const showFormat = !showContext && needsFormat(workflow);
  const showCharacters = !showContext && !showFormat && needsCharacters(workflow);
  const [context, setContext] = useState(null);
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [scriptOpen, setScriptOpen] = useState(false);
  const scriptStarted = useRef('');
  const builderOpened = useRef('');
  const scriptShown = useRef('');
  const scriptDismissed = useRef(false);
  const onPlanRef = useRef(onPlan);
  const onScriptRef = useRef(onScript);
  const onOpenBuilderRef = useRef(onOpenBuilder);
  onPlanRef.current = onPlan;
  onScriptRef.current = onScript;
  onOpenBuilderRef.current = onOpenBuilder;

  useEffect(() => {
    if (!plan?.id || (!showContext && !showCharacters)) return undefined;
    let cancelled = false;
    fetchStoryContext(plan.id)
      .then((data) => {
        if (cancelled) return;
        setContext(data?.context || null);
        const graph = data?.context?.graph || {};
        setEntities(
          (graph.entities || [])
            .filter((entity) => isCharacterName(entity.display_name) && entity.review_status !== 'rejected' && entity.story_character !== false)
            .map((entity) => ({
              entity_id: entity.entity_id,
              display_name: entity.display_name,
              description: entity.description || '',
              traits: entity.personality_traits || entity.traits || [],
            })),
        );
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err, 'Could not load the story'));
      });
    return () => {
      cancelled = true;
    };
  }, [plan?.id, showContext, showCharacters, workflow.draft_version_id, workflow.state]);

  useEffect(() => {
    if (!showCharacters || !workflow.script) return undefined;
    const key = `${plan?.id}:${workflow.script}`;
    if (scriptShown.current === key) return undefined;
    scriptShown.current = key;
    scriptDismissed.current = false;
    setScriptOpen(true);
    return undefined;
  }, [plan?.id, showCharacters, workflow.script]);

  useEffect(() => {
    if (!plan?.id || !showCharacters || workflow.script) return undefined;
    if (workflow?.story_format?.status !== 'locked') return undefined;
    if (scriptStarted.current === `${plan.id}:done`) return undefined;
    scriptStarted.current = plan.id;
    let cancelled = false;
    setBusy('script');
    setError('');
    draftStoryScript(plan.id)
      .then((data) => {
        if (cancelled) return;
        scriptStarted.current = `${plan.id}:done`;
        onPlanRef.current?.(data.plan);
        setContext(data.context);
        const names = (data.characters || []).map((entity) => entity.display_name).filter(Boolean);
        onScriptRef.current?.(data.script || '', names);
        setEntities(
          (data.characters || [])
            .filter((entity) => isCharacterName(entity.display_name))
            .map((entity) => ({
              entity_id: entity.entity_id,
              display_name: entity.display_name,
              description: entity.description || '',
              traits: entity.personality_traits || entity.traits || [],
            })),
        );
      })
      .catch((err) => {
        if (scriptStarted.current === plan.id) scriptStarted.current = '';
        if (!cancelled) setError(formatApiError(err, 'Could not write the script'));
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });
    return () => {
      cancelled = true;
    };
  }, [plan?.id, showCharacters, workflow.script, workflow?.story_format?.status]);

  useEffect(() => {
    if (!scriptDismissed.current || scriptOpen || !showCharacters || !entities.length || !workflow.script) return undefined;
    const names = entities.map((entity) => entity.display_name);
    const key = `${plan?.id}:${names.join(',')}`;
    if (builderOpened.current === key) return undefined;
    builderOpened.current = key;
    onOpenBuilderRef.current?.(entities);
    return undefined;
  }, [plan?.id, showCharacters, entities, scriptOpen, workflow.script]);

  if (!showContext && !showFormat && !showCharacters) return null;

  const versionId = workflow.draft_version_id || context?.version_id;
  const names = entities.map((entity) => entity.display_name);

  const patchContext = async (body) => {
    setBusy('patch');
    setError('');
    try {
      const data = await patchStoryContext(plan.id, { version_id: versionId, ...body });
      if (data?.plan) onPlan?.(data.plan);
      if (data?.context) setContext(data.context);
    } catch (err) {
      setError(formatApiError(err, 'Could not save that edit'));
    } finally {
      setBusy('');
    }
  };

  const closeScript = () => {
    scriptDismissed.current = true;
    setScriptOpen(false);
  };

  const saveFormat = async (recommendation, action) => {
    setBusy(action);
    setError('');
    try {
      const data = await updateStoryFormat(plan.id, {
        action,
        format_id: workflow.story_format?.format_id,
        recommendation,
      });
      if (data?.plan) onPlan?.(data.plan);
    } catch (err) {
      setError(formatApiError(err, 'Could not save the storytelling format'));
    } finally {
      setBusy('');
    }
  };

  const confirm = async () => {
    setBusy('confirm');
    setError('');
    try {
      const data = await confirmStoryContext(plan.id, { version_id: versionId });
      if (data?.plan) onPlan?.(data.plan);
      if (data?.context) setContext(data.context);
    } catch (err) {
      setError(formatApiError(err, 'Could not lock this version'));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="pl-9 max-w-lg" data-testid="story-chat-questionnaire">
      {error ? <p className="mb-2 text-xs text-red-300">{error}</p> : null}
      {showContext ? (
        workflow.state === 'analyzing_sources' && !context?.graph ? (
          <p className="text-xs text-[#A78BFA] flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Reading your story…
          </p>
        ) : (
          <StoryContextReview
            mode="brief"
            summary={context?.graph?.summary}
            manifest={context?.manifest || []}
            entities={context?.graph?.entities || []}
            claims={context?.graph?.claims || []}
            questions={context?.graph?.questions || []}
            versionId={versionId}
            reconfirmNotice={workflow.reconfirm_notice}
            confirmRequested={Boolean(workflow.confirm_requested)}
            busy={busy}
            onPatch={patchContext}
            onLock={confirm}
          />
        )
      ) : null}
      {showFormat ? (
        <StoryFormatReview
          key={`${workflow.story_format?.format_id || ''}:${workflow.story_format?.status || ''}`}
          storyFormat={workflow.story_format}
          storytellingOptions={plan?.field_questions?.['flow_params.story_format']?.options}
          staleNotice={workflow.format_stale_notice}
          busy={busy}
          onConfirm={(recommendation) => saveFormat(recommendation, 'confirm')}
          onEdit={(recommendation) => saveFormat(recommendation, 'edit')}
        />
      ) : null}
      {showCharacters ? (
        <div className="space-y-3" data-testid="story-chat-characters">
          {busy === 'script' ? (
            <p className="text-xs text-[#E5E7EB] flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Writing the script, then reading its characters.
            </p>
          ) : null}
          {workflow.script && !scriptOpen ? (
            <button
              type="button"
              onClick={() => setScriptOpen(true)}
              className="text-xs text-[#5EEAD4] underline cursor-pointer"
              data-testid="story-chat-reopen-script"
            >
              Open the script
            </button>
          ) : null}
          {!scriptOpen && names.length ? (
            <div className="rounded-xl border border-white/10 p-3 space-y-2">
              <p className="text-xs text-[#9CA3AF]">
                These characters are in the script: {names.join(', ')}.
              </p>
              <button
                type="button"
                onClick={() => onOpenBuilder?.(entities)}
                className="text-xs text-[#5EEAD4] underline cursor-pointer"
                data-testid="story-chat-open-builder"
              >
                Open character builder
              </button>
            </div>
          ) : null}
          {!scriptOpen && !names.length && busy !== 'script' ? (
            <p className="text-xs text-[#E5E7EB]">No characters were found in the script yet.</p>
          ) : null}
        </div>
      ) : null}
      {scriptOpen && workflow.script ? (
        <div
          className="fixed inset-0 z-[230] flex items-center justify-center bg-black/70 p-4"
          role="presentation"
          onClick={closeScript}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Script"
            data-testid="story-script-popup"
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-white/15 bg-[#0c1018] shadow-2xl"
          >
            <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
              <div>
                <h3 className="text-sm font-semibold text-white">Script</h3>
                <p className="text-[11px] text-[#9CA3AF] mt-1">
                  Written from the format you confirmed. Close this to build the characters.
                </p>
              </div>
              <button
                type="button"
                onClick={closeScript}
                className="w-8 h-8 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white flex items-center justify-center cursor-pointer"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <pre className="p-4 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-[#E5E7EB] font-sans">{workflow.script}</pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}
